'use client';
import { useCallback, useEffect, useState } from 'react';

import { checkStandalone, trackStandaloneChange } from '../helpers';
import { safeClientCallback } from '@/modules/server/helpers-client';

export enum Platform {
  DESKTOP = 'desktop',
  IOS = 'ios',
  ANDROID = 'android',
}

/**
 * This hook is used to determine the app environment based on the user agent and display mode.
 * It sets the environment to either 'desktop', 'mobile', 'desktop'.
 * @returns {string} The app environment.
 */
const checkUserAgentPlatform = safeClientCallback((): Platform => {
  const userAgent = navigator.userAgent.toLowerCase();

  const isAndroid = /android/.test(userAgent);
  const isIOS = /iphone|ipad|ipod/.test(userAgent);

  let platform = Platform.DESKTOP;
  if (isIOS) platform = Platform.IOS;
  if (isAndroid) platform = Platform.ANDROID;

  return platform;
}, Platform.DESKTOP);

export function useAppEnvironment(): string {
  const defaultPlatform = checkUserAgentPlatform();
  const defaultAppType = checkStandalone() ? 'pwa' : 'web';
  const [env, setEnv] = useState<string>(
    `${defaultAppType}-${defaultPlatform}`
  );

  const checkPlatformType = useCallback((isStandalone: boolean) => {
    const platform = checkUserAgentPlatform();
    setEnv(`${isStandalone ? 'pwa' : 'web'}-${platform}`);
  }, []);

  useEffect(() => {
    const unsubscribe = trackStandaloneChange(checkPlatformType);
    return unsubscribe;
  }, [checkPlatformType]);

  return env;
}
